import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { BookOpen, Brain, BarChart3, User, CheckCircle2, XCircle, Video, Lock } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useFont } from '../contexts/FontContext';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../hooks/useSubscription';

export default function Dashboard() {
  const { theme } = useTheme();
  const { font } = useFont();
  const { user } = useAuth();
  const { hasActiveSubscription } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();
  const { search } = location;
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [countdown, setCountdown] = useState(5);
  const [stripeMessage, setStripeMessage] = useState<string | null>(null);
  const [showCountdownBanner, setShowCountdownBanner] = useState(false);

  const canBookGroupCalls = user?.subscription?.includes('DETERMINED_');
  const canBookIndividualCalls = user?.subscription === 'DETERMINED_ANNUALLY';

  const getGroupCallLink = () => {
    if (user?.subscription?.includes('DETERMINED_')) {
      return 'https://calendly.com/loustaletformation/formation-de-groupe-abonne';
    }
    return 'https://calendly.com/loustaletformation/formation-de-groupe-abonne-clone';
  };

  const getIndividualCallLink = () => {
    if (user?.subscription === 'DETERMINED_ANNUALLY') {
      return 'https://calendly.com/loustaletformation/30min';
    }
    return 'https://calendly.com/loustaletformation/rendez-vous-individuel-abonne-clone';
  };

  useEffect(() => {
    console.log('[Dashboard] URL search params:', location.search);
    console.log('[Dashboard] Parsed URL params:', Object.fromEntries(new URLSearchParams(location.search).entries()));

    const searchParams = new URLSearchParams(search);
    const subscription = searchParams.get('subscription');
    const upgrade = searchParams.get('upgrade');

    console.log('[Dashboard] subscription param:', subscription);
    console.log('[Dashboard] upgrade param:', upgrade);

    if (subscription === 'success') {
      console.log('[Dashboard] Stripe redirect detected, starting countdown');

      // Clean up URL immediately to prevent re-triggering
      const newUrl = window.location.pathname;
      window.history.replaceState(null, '', newUrl);

      setShowCountdownBanner(true);
      setCountdown(5);

      const upgradeText = upgrade === 'true'
        ? 'Votre abonnement a été mis à jour avec succès !'
        : 'Votre abonnement a été activé avec succès !';

      setStripeMessage(`${upgradeText} Redirection dans ${countdown} secondes...`);

      // Start countdown
      const countdownInterval = setInterval(() => {
        setCountdown(prev => {
          console.log('[Dashboard] Countdown:', prev - 1);
          const newCount = prev - 1;

          if (newCount <= 0) {
            console.log('[Dashboard] Countdown finished, clearing message');
            clearInterval(countdownInterval);
            setStripeMessage(null);
            setShowCountdownBanner(false);
            return 0;
          }

          // Update message with new countdown
          setStripeMessage(`${upgradeText} Redirection dans ${newCount} secondes...`);
          return newCount;
        });
      }, 1000);

      setMessage({
        type: 'success',
        text: upgrade === 'true'
          ? 'Votre abonnement a été mis à jour avec succès !'
          : 'Votre abonnement a été activé avec succès ! Vous pouvez maintenant accéder à tous les contenus.'
      });

      // Cleanup function
      return () => {
        console.log('[Dashboard] Cleaning up countdown interval');
        clearInterval(countdownInterval);
      };
    }
  }, [search]);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
    }
  }, [user, navigate]);

  // Show loading state while checking authentication
  if (!user) return null;

  const hasAccess = hasActiveSubscription();
  const isQCMLocked = !hasAccess;
  const isStatsLocked = !hasAccess;

  const menuItems = [
    {
      icon: BookOpen,
      label: 'Les cours',
      path: '/cours',
      locked: !hasAccess,
      lockReason: 'Abonnement requis'
    },
    {
      icon: Brain,
      label: 'QCM',
      path: '/qcm',
      locked: isQCMLocked,
      lockReason: 'Abonnement requis'
    },
    {
      icon: BarChart3,
      label: 'Statistiques',
      path: '/stats',
      locked: isStatsLocked,
      lockReason: 'Abonnement requis'
    },
    {
      icon: User,
      label: 'Profil',
      path: '/profil',
      locked: false
    },
  ];

  const handleMenuClick = (item: typeof menuItems[0]) => {
    if (item.locked) {
      navigate('/tarifs');
    } else {
      navigate(item.path);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: font.family }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Trial active banner */}
        {user.trial_active && user.trial_end && (() => {
          const trialEnd = new Date(user.trial_end);
const now = new Date();

const diffMs = trialEnd.getTime() - now.getTime();
let remainingStr;
// Si déjà expiré
if (diffMs <= 0) {
  remainingStr = 'Essai expiré';
} else {
  const totalMinutes = Math.floor(diffMs / (1000 * 60));
  const totalHours = Math.floor(diffMs / (1000 * 60 * 60));
  const totalDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  const remainingHours = totalHours % 24;
  const remainingMinutes = totalMinutes % 60;

  if (totalDays > 1) {
    remainingStr = `${totalDays} jours restants`;
  } else if (totalDays === 1) {
    remainingStr = '1 jour restant';
  } else if (remainingHours > 1) {
    remainingStr = `${remainingHours} heures restantes`;
  } else if (remainingHours === 1) {
    remainingStr = '1 heure restante';
  } else if (remainingMinutes > 1) {
    remainingStr = `${remainingMinutes} minutes restantes`;
  } else if (remainingMinutes === 1) {
    remainingStr = '1 minute restante';
  } else {
    remainingStr = 'Moins d’une minute restante';
  }
}

const endDateStr = trialEnd.toLocaleDateString('fr-FR', {
  day: 'numeric',
  month: 'long',
  year: 'numeric'
});
          return (
            <div className="bg-blue-50 border-2 border-blue-400 rounded-xl shadow-sm p-5 max-w-4xl mx-auto mb-8">
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="flex-shrink-0 bg-blue-100 rounded-full p-3">
                  <Lock className="h-6 w-6 text-blue-600" />
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <h2 className="text-lg font-bold text-gray-800">
                    Vous êtes en période d'essai
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Votre période d'essai se termine le <strong>{endDateStr}</strong>.
                  </p>
                  <p className="text-sm font-semibold text-blue-700 mt-0.5">
                    {remainingStr}
                  </p>
                </div>
                <button
                  onClick={() => navigate('/tarifs')}
                  className="flex-shrink-0 bg-blue-600 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-blue-700 transition-colors text-sm whitespace-nowrap"
                >
                  S'abonner maintenant
                </button>
              </div>
            </div>
          );
        })()}

        {/* Subscription required banner - shown at the very top */}
        {!hasAccess && !user.trial_active && (
          <div className="bg-amber-50 border-2 border-amber-400 rounded-xl shadow-sm p-5 max-w-4xl mx-auto mb-8">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="flex-shrink-0 bg-amber-100 rounded-full p-3">
                <Lock className="h-6 w-6 text-amber-600" />
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h2 className="text-lg font-bold text-gray-800">
                  Votre periode d'essai est terminée
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Abonnez-vous pour accéder aux cours, aux QCM et aux statistiques.
                </p>
              </div>
              <button
                onClick={() => navigate('/tarifs')}
                className="flex-shrink-0 bg-amber-600 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-amber-700 transition-colors text-sm whitespace-nowrap"
              >
                Voir les formules
              </button>
            </div>
          </div>
        )}

        {stripeMessage && (
          <div className="mb-8 p-6 rounded-lg bg-green-50 border border-green-200">
            <div className="flex items-center">
              <CheckCircle2 className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-green-700 font-medium text-lg">
                  {stripeMessage}
                </p>
                <div className="mt-3 w-full bg-green-200 rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full transition-all duration-1000 ease-linear"
                    style={{ width: `${((5 - countdown) / 5) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {message && (
          <div className={`mb-8 p-4 rounded-lg flex items-start ${
            message.type === 'success' 
              ? 'bg-green-50 border border-green-200' 
              : 'bg-red-50 border border-red-200'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
            ) : (
              <XCircle className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
            )}
            <p className={message.type === 'success' ? 'text-green-700' : 'text-red-700'}>
              {message.text}
            </p>
          </div>
        )}

        {!showCountdownBanner && (
          <>
            <h1 className={`text-3xl font-bold text-${theme.text} mb-8 text-center`}>
              Tableau de bord
            </h1>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {menuItems.map((item) => {
                const { icon: Icon, label, path, locked, lockReason } = item;
                return (
                <button
                  key={path}
                  onClick={() => handleMenuClick(item)}
                  className={`bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow 
                    border border-gray-200 hover:border-${theme.primary}
                    flex items-center space-x-4 group relative ${locked ? 'opacity-75' : ''}`}
                >
                  <div className={`p-3 rounded-lg ${locked ? 'bg-gray-200' : `bg-${theme.secondary} group-hover:bg-${theme.primary}`} transition-all duration-200`}>
                    <Icon className={`h-8 w-8 ${locked ? 'text-gray-400' : `text-${theme.text} group-hover:text-black`} transition-colors duration-200`} />
                  </div>
                  <span className={`text-xl font-medium ${locked ? 'text-gray-500' : `text-gray-700 group-hover:text-${theme.primary}`} transition-colors duration-200`}>
                    {label}
                  </span>
                  {locked && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 rounded-xl">
                      <div className="text-center">
                        <div className="w-8 h-8 mx-auto mb-2 bg-gray-100 rounded-full flex items-center justify-center">
                          <Lock className="w-4 h-4 text-gray-600" />
                        </div>
                        <p className="text-xs text-gray-600 font-medium max-w-24">{lockReason}</p>
                      </div>
                    </div>
                  )}
                </button>
                );
              })}
            </div>

            {/* Video Call Booking Section - Only for subscribed users */}
            {hasActiveSubscription() && (
              <div className="bg-white rounded-xl shadow-sm p-6 max-w-4xl mx-auto mt-8">
                <h2 className="text-xl font-semibold mb-6 text-center">Réserver une visioconférence</h2>
                <div className="grid grid-cols-1 md:grid-cols-1 justify-items-center gap-6">
                  <div className={`bg-${theme.secondary} rounded-lg p-6`}>
                    <div className="flex items-center mb-4">
                      <Video className={`h-8 w-8 text-${theme.text} mr-3`} />
                      <div>
                        <h3 className="text-lg font-semibold">Visio individuelle</h3>
                        <p className="text-sm text-gray-500">
                          {canBookIndividualCalls ? 'Gratuit' : '50€ par session'}
                        </p>
                      </div>
                    </div>
                    <p className="text-gray-600 mb-6">
                      Bénéficiez d'un accompagnement personnalisé en tête-à-tête.
                    </p>
                    <a
                      href={getIndividualCallLink()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`inline-flex items-center px-6 py-3 rounded-lg w-full justify-center border-2 border-${theme.primary} text-${theme.text} hover:bg-${theme.secondary} transition-colors`}
                    >
                      <Video className="h-5 w-5 mr-2" />
                      Réserver une session individuelle
                    </a>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Free user promotion */}
      </div>
    </div>
  );
}